import { paraFormat } from '../analysis/format';
import { paginate } from '../analysis/pages';
import { cleanText, SHORT_WORK_KINDS, STRUCTURAL_TITLES, type Heading, type Role, type StructuralKind } from '../analysis/structure';
import { builtInHeadingLevel, type Paragraph } from '../model/document';
import { GOST_2_105, GOST_7_32 } from '../profiles/ids';
import type { Profile } from '../profiles/types';
import { APPENDIX_LETTERS_CYR, APPENDIX_LETTERS_LAT, countOf, isAllCaps, normalizeForCompare, startsWithUpper, upperFirst } from '../util/text';
import { cmToTwips, fmtNum } from '../util/units';
import { defineRule, type FixAction, type Rule, type RuleContext } from './types';
import { byRole, estimatePages, hasTrailingPeriod, mainFont, mainTextSize, quote, trailingPeriodFix, upperFirstFix } from './util';

const NUM_PREFIX = /^\d{1,2}(?:\.\d{1,2})*\.?\s+/;
const dropNumber = (s: string): string => s.replace(NUM_PREFIX, '');
const firstLine = (s: string): string => s.split('\n')[0]!.trim();
const label = (h: Heading): string => (h.kind === 'section' && h.number ? `${h.number} ${h.title}` : dropNumber(h.title));
const para = (ctx: RuleContext, i: number): Paragraph => ctx.model.paragraphs[i]!;
const STATUS_RE = /^\(?\s*(обязательное|рекомендуемое|справочное|информационное)\s*\)?\.?$/i;

/** Название структурного элемента так, как его пишут по стандарту профиля. */
function titleOf(profile: Profile, kind: StructuralKind): string {
  const name = profile.headings.names[kind];
  if (name) return name;
  const upper = STRUCTURAL_TITLES[kind];
  return profile.headings.structuralCase === 'sentence' ? upperFirst(upper.toLowerCase()) : upper;
}

// ─── Автоматическое содержание ──────────────────────────────────────────

type TocTarget = { replace: { first: number; last: number } } | { after: number } | { before: number };

/** Абзацы старого содержания вместе с пустыми строками и концом поля TOC. */
function tocRange(ctx: RuleContext): { first: number; last: number } | undefined {
  const entries = ctx.s.tocEntries;
  if (!entries.length) return undefined;
  const paras = ctx.model.paragraphs;
  const isTocTail = (i: number): boolean => {
    const p = paras[i];
    return !!p && p.inToc && !ctx.s.headingByPara.has(i) && !p.cell && !p.textBox;
  };
  let first = entries[0]!;
  let last = entries[entries.length - 1]!;
  while (isTocTail(last + 1)) last++;
  while (isTocTail(first - 1)) first--;
  const parent = paras[first]!.el.parent;
  for (let i = first; i <= last; i++) {
    const p = paras[i]!;
    if (p.cell || p.textBox || p.el.parent !== parent) return undefined;
  }
  // Поле TOC, начатое в самом заголовке «Содержание», заменять по частям нельзя.
  const heading = paras[first - 1];
  if (heading && ctx.s.headingByPara.has(heading.index) && heading.fields.some((f) => f.type === 'TOC')) return undefined;
  return { first, last };
}

/** Текст пункта: так, как его запишет Word, — номер из автонумерации и первая строка заголовка без точки. */
function tocEntryText(ctx: RuleContext, h: Heading): string {
  const p = para(ctx, h.paragraph);
  const auto = p.list && !p.list.bullet ? `${p.list.label.trim()} ` : '';
  return (auto + firstLine(cleanText(p))).replace(/(?<!\.)[.:;]+$/, '').trim();
}

/**
 * Исправление «собрать содержание автоматически»: заголовкам — уровни структуры (по ним Word
 * обновляет поле), в документ — поле TOC с гиперссылками и приблизительными номерами страниц.
 */
function tocFix(ctx: RuleContext, target: TocTarget): FixAction[] | undefined {
  const { model, s, profile } = ctx;
  const paras = model.paragraphs;
  const depth = Math.max(1, Math.min(3, profile.headings.tocDepth));
  const from = 'replace' in target ? target.replace.last : 'after' in target ? target.after : target.before - 1;
  const heads = s.headings.filter(
    (h) =>
      !h.inferred &&
      h.structural !== 'contents' &&
      h.paragraph > from &&
      (h.kind !== 'section' || (h.level <= depth && s.regions[h.paragraph] !== 'appendix')),
  );
  if (!heads.length) return undefined;
  const pageOf = paginate(model).pageOf;
  // Новое содержание со своей страницей сдвигает номера страниц за ним.
  const shift = 'before' in target ? 1 + Math.floor(heads.length / 25) : 0;
  const actions: FixAction[] = [];
  const entries: Array<{ paragraph: number; level: number; text: string; page: number }> = [];
  for (const h of heads) {
    const p = paras[h.paragraph]!;
    if (p.cell || p.textBox) continue;
    const level = h.kind === 'section' ? h.level : 1;
    // У встроенных стилей «Заголовок N» Word уровень структуры не меняет — ставим его только остальным абзацам.
    if (p.props.outlineLvl !== level - 1 && builtInHeadingLevel(model.styles, p.styleId) === undefined) {
      actions.push({ kind: 'para', paragraph: p.index, set: { outlineLvl: level - 1 } });
    }
    entries.push({ paragraph: p.index, level, text: tocEntryText(ctx, h), page: (pageOf[p.index] ?? 1) + shift });
  }
  const anchor = paras['replace' in target ? target.replace.first : 'after' in target ? target.after : target.before]!;
  const sec = model.sections[anchor.section];
  const width = (sec?.pageWidth ?? 11906) - (sec?.margins.left ?? 1701) - (sec?.margins.right ?? 850) - (sec?.margins.gutter ?? 0);
  const title = 'before' in target ? { text: titleOf(profile, 'contents'), bold: profile.headings.structuralBold } : undefined;
  actions.push({
    kind: 'toc',
    ...target,
    title,
    entries,
    depth,
    tabPos: Math.max(2000, width),
    font: mainFont(ctx).font,
    sz: mainTextSize(ctx),
    line: Math.round(profile.text.lineSpacing * 240),
  });
  // Содержание занимает свою страницу: следующий за ним заголовок — с новой страницы.
  if ('before' in target && !anchor.props.pageBreakBefore) actions.push({ kind: 'para', paragraph: anchor.index, set: { pageBreakBefore: true } });
  return actions;
}

export const structureRules: Rule[] = [
  defineRule({
    id: 'structure.required',
    category: 'structure',
    title: 'Обязательные структурные элементы',
    severity: 'error',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, раздел 4', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.1.2, 6.2.1' },
    why: {
      [GOST_7_32]: 'В работе должны быть содержание, введение, заключение и список использованных источников. Точный набор задаёт методичка. Для лабораторных и практических работ эти требования не проверяются.',
      [GOST_2_105]: 'Обязательны титульный лист и основной текст. Содержание, как правило, включают, если в документе больше 24 листов. Остальной набор элементов задаёт методичка.',
    },
    check(ctx) {
      const { s, profile } = ctx;
      // В лабораторных, практических и контрольных работах введения и заключения обычно нет.
      if (SHORT_WORK_KINDS.has(s.kind)) return;
      const pages = estimatePages(ctx.model);
      // Один титульный лист или короткий отчёт о задании — не работа, у которой проверяют структуру.
      if (pages <= 3 || ((s.kind === 'unknown' || s.kind === 'report') && pages <= 10)) return;
      const found = new Set(s.headings.map((h) => h.structural));
      for (const kind of profile.headings.required) {
        if (found.has(kind)) continue;
        if (kind === 'contents' && pages <= profile.headings.contentsMinPages) continue;
        let fix: FixAction[] | undefined;
        if (kind === 'contents') {
          // Содержание вставляем перед первым заголовком после титульного листа.
          const first = s.headings.find((h) => !h.inferred && h.paragraph >= s.frontEnd && (h.kind !== 'section' || h.level === 1));
          const p = first && para(ctx, first.paragraph);
          if (p && !p.cell && !p.textBox) fix = tocFix(ctx, { before: p.index });
        }
        ctx.report({
          message: `Не найден элемент «${titleOf(profile, kind)}». Если он есть, проверьте, что заголовок стоит отдельной строкой без лишних слов`,
          fix,
          fixLabel: 'Собрать содержание автоматически',
        });
      }
    },
  }),

  defineRule({
    id: 'structure.naming',
    category: 'structure',
    title: 'Названия структурных элементов',
    severity: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.2.5, 6.4.1' },
    why: {
      [GOST_7_32]: 'Стандарт задаёт точные названия: «СОДЕРЖАНИЕ», «СПИСОК ИСПОЛЬЗОВАННЫХ ИСТОЧНИКОВ». Некоторые методички допускают другие — сверьтесь со своей.',
      [GOST_2_105]: 'Стандарт называет элементы «Содержание» и «Библиография». Многие методички допускают «Список использованных источников» — сверьтесь со своей.',
    },
    check(ctx) {
      const { profile } = ctx;
      const norm = (s: string): string => s.toUpperCase().replace(/Ё/g, 'Е').replace(/[.:;]+$/, '').replace(/\s+/g, ' ');
      for (const h of ctx.s.headings) {
        const canon = h.structural && profile.headings.names[h.structural];
        if (!canon) continue;
        // «Выводы» в лабораторной работе — обычное название, «Заключение» там не требуют.
        if (h.structural === 'conclusion' && SHORT_WORK_KINDS.has(ctx.s.kind)) continue;
        if (norm(dropNumber(firstLine(h.title))) === norm(canon)) continue;
        const p = para(ctx, h.paragraph);
        const start = p.text.search(/\S/);
        const end = p.text.trimEnd().length;
        const whole = !h.number && !p.text.includes('\n') && start >= 0;
        // Если стандарт пишет название с прописной буквы, а в работе все заголовки прописными, сохраняем стиль работы.
        const replace = profile.headings.structuralCase === 'sentence' && isAllCaps(h.title) ? canon.toUpperCase() : canon;
        ctx.report({
          paragraph: h.paragraph,
          message: `Элемент называется ${quote(h.title, 50)}, по ${profile.name} — «${canon}»`,
          fix: whole ? [{ kind: 'text', paragraph: p.index, start, end, expect: p.text.slice(start, end), replace }] : undefined,
          fixLabel: `Переименовать в «${replace}»`,
        });
      }
    },
  }),

  defineRule({
    id: 'structure.order',
    category: 'structure',
    title: 'Порядок структурных элементов',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, раздел 4', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.1.1' },
    why: {
      [GOST_7_32]: 'Порядок: титульный лист, реферат, содержание, введение, основная часть, заключение, список источников, приложения.',
      [GOST_2_105]: 'Порядок: титульный лист, предисловие, содержание, обозначения и сокращения, термины и определения, основной текст, приложения и библиография.',
    },
    check(ctx) {
      const groups = ctx.profile.headings.order;
      const rank = (h: Heading): number => (h.structural ? groups.findIndex((g) => g.includes(h.structural!)) : -1);
      const hs = ctx.s.headings;
      let prev: Heading | undefined;
      let reported = 0;
      for (const h of hs) {
        if (rank(h) < 0) continue;
        if (prev && rank(h) < rank(prev) && reported < 3) {
          ctx.report({ paragraph: h.paragraph, message: `${quote(label(h), 40)} стоит после ${quote(label(prev), 40)} — по стандарту наоборот` });
          reported++;
        }
        prev = h;
      }
      const intro = hs.find((h) => h.structural === 'intro');
      const conclusion = hs.find((h) => h.structural === 'conclusion');
      const sections = hs.filter((h) => h.kind === 'section' && h.level === 1 && !h.inferred && ctx.s.regions[h.paragraph] === 'main');
      const early = intro && sections.find((h) => h.paragraph < intro.paragraph);
      if (early) ctx.report({ paragraph: early.paragraph, message: `Раздел ${quote(label(early), 40)} стоит до введения` });
      const late = conclusion && sections.find((h) => h.paragraph > conclusion.paragraph);
      if (late) ctx.report({ paragraph: late.paragraph, message: `Раздел ${quote(label(late), 40)} стоит после заключения` });
    },
  }),

  defineRule({
    id: 'structure.structural-format',
    category: 'structure',
    title: 'Заголовки структурных элементов',
    severity: 'warning',
    shortWork: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.1, 6.17.3', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.2.5, 6.3.4' },
    why: {
      [GOST_7_32]: 'Заголовки «ВВЕДЕНИЕ», «ЗАКЛЮЧЕНИЕ» и другие располагают по центру, прописными буквами, без точки в конце, не подчёркивая.',
      [GOST_2_105]: 'Слово «Содержание» пишут посередине страницы с прописной буквы и выделяют полужирным; «Приложение» с обозначением — наверху посередине страницы.',
    },
    check(ctx) {
      const { model, s, profile } = ctx;
      const upper = profile.headings.structuralCase === 'upper';
      for (const h of s.headings) {
        if (h.kind === 'section') continue;
        const p = para(ctx, h.paragraph);
        const fmt = paraFormat(model, p);
        const text = cleanText(p);
        const head = firstLine(p.text);
        const multiLine = p.text.includes('\n');
        const problems: string[] = [];
        const fix: FixAction[] = [];
        if (p.props.jc !== 'center') {
          problems.push('не по центру');
          fix.push({ kind: 'para', paragraph: p.index, set: { jc: 'center', indFirst: 0, indLeft: 0 } });
        } else if (Math.abs(p.props.indFirst ?? 0) > 20 || Math.abs(p.props.indLeft ?? 0) > 20) {
          problems.push('сдвинут абзацным отступом');
          fix.push({ kind: 'para', paragraph: p.index, set: { indFirst: 0, indLeft: 0 } });
        }
        const capsPart = h.kind === 'appendix' ? head.split(/\s+/)[0]! : head;
        if (upper) {
          if (!isAllCaps(capsPart) && fmt.capsShare < 0.9) {
            problems.push('не прописными буквами');
            if (!multiLine) fix.push({ kind: 'runs', paragraph: p.index, set: { caps: true } });
          }
        } else if (!startsWithUpper(capsPart)) {
          problems.push('со строчной буквы');
          const f = upperFirstFix(p);
          if (f) fix.push(f);
        }
        if (h.kind !== 'appendix' && hasTrailingPeriod(text)) {
          problems.push('точка в конце');
          const f = trailingPeriodFix(p);
          if (f) fix.push(f);
        }
        if (profile.headings.structuralBold && fmt.boldShare < 0.6) {
          problems.push('не полужирный');
          fix.push({ kind: 'runs', paragraph: p.index, set: { bold: true } });
        }
        if (fmt.underlineShare > 0.3) {
          problems.push('подчёркнут');
          fix.push({ kind: 'runs', paragraph: p.index, set: { underline: false } });
        }
        if (!problems.length) continue;
        ctx.report({ paragraph: p.index, message: `Заголовок ${quote(head, 40)}: ${problems.join(', ')}`, fix, fixLabel: 'Оформить заголовок' });
      }
    },
  }),

  defineRule({
    id: 'structure.structural-numbered',
    category: 'structure',
    title: 'Структурные элементы без номера',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.1, 6.4.1', [GOST_2_105]: '' },
    why: 'Нумеруют только разделы основной части. «Введение», «Заключение», содержание и список источников не нумеруют.',
    check(ctx) {
      for (const h of ctx.s.headings) {
        if (h.kind !== 'structural' || !h.number) continue;
        const p = para(ctx, h.paragraph);
        let fix: FixAction[] | undefined;
        if (h.numberSource === 'auto') {
          fix = [{ kind: 'para', paragraph: p.index, set: { removeNumbering: true } }];
        } else {
          const m = /^(\s*)(\d{1,2}(?:\.\d{1,2})*\.?\s+)/.exec(p.text);
          if (m) fix = [{ kind: 'text', paragraph: p.index, start: m[1]!.length, end: m[0].length, expect: m[2]!, replace: '' }];
        }
        ctx.report({ paragraph: p.index, message: `${quote(dropNumber(h.title), 40)} — структурный элемент, его не нумеруют`, fix, fixLabel: 'Убрать номер' });
      }
    },
  }),

  defineRule({
    id: 'structure.heading-format',
    category: 'structure',
    title: 'Заголовки разделов и подразделов',
    severity: 'warning',
    shortWork: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.3', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.5.4, 6.6.2' },
    why: 'Заголовки разделов и подразделов начинают с абзацного отступа после номера, печатают с прописной буквы полужирным, без точки в конце и без подчёркивания.',
    check(ctx) {
      const indent = cmToTwips(ctx.profile.text.firstLineIndentCm);
      const indentMax = cmToTwips(ctx.profile.text.firstLineIndentMaxCm ?? ctx.profile.text.firstLineIndentCm);
      const align = ctx.profile.headings.sectionAlign;
      for (const h of ctx.s.headings) {
        // Угаданные по оформлению заголовки («Цель работы») не проверяем: это может быть и не заголовок.
        if (h.kind !== 'section' || h.inferred) continue;
        const p = para(ctx, h.paragraph);
        const fmt = paraFormat(ctx.model, p);
        const problems: string[] = [];
        const fix: FixAction[] = [];
        if (ctx.profile.headings.sectionBold && fmt.boldShare < 0.6) {
          problems.push('не полужирный');
          fix.push({ kind: 'runs', paragraph: p.index, set: { bold: true } });
        }
        if (!startsWithUpper(h.title)) {
          problems.push('со строчной буквы');
          const f = upperFirstFix(p);
          if (f) fix.push(f);
        }
        if (hasTrailingPeriod(h.title)) {
          problems.push('точка в конце');
          const f = trailingPeriodFix(p);
          if (f) fix.push(f);
        }
        if (fmt.underlineShare > 0.3) {
          problems.push('подчёркнут');
          fix.push({ kind: 'runs', paragraph: p.index, set: { underline: false } });
        }
        const start = (p.props.indLeft ?? 0) + (p.props.indFirst ?? 0);
        const jc = p.props.jc ?? 'left';
        if (align === 'indent' && (jc === 'center' || start < indent - 40 || start > indentMax + 40)) {
          problems.push('не с абзацного отступа');
          fix.push({ kind: 'para', paragraph: p.index, set: { jc: 'left', indFirst: indent, indLeft: 0 } });
        } else if (align === 'center' && jc !== 'center') {
          problems.push('не по центру');
          fix.push({ kind: 'para', paragraph: p.index, set: { jc: 'center', indFirst: 0, indLeft: 0 } });
        } else if (align === 'left' && (jc === 'center' || Math.abs(start) > 40)) {
          problems.push('не от левого края');
          fix.push({ kind: 'para', paragraph: p.index, set: { jc: 'left', indFirst: 0, indLeft: 0 } });
        }
        if (!problems.length) continue;
        ctx.report({ paragraph: p.index, message: `Заголовок ${quote(label(h), 45)}: ${problems.join(', ')}`, fix, fixLabel: 'Оформить заголовок' });
      }
    },
  }),

  defineRule({
    id: 'structure.heading-size',
    category: 'structure',
    title: 'Размер шрифта заголовков',
    severity: 'info',
    gost: 'ГОСТ Р 2.105-2019, п. 6.6.2',
    why: 'Заголовки разделов выделяют увеличенным размером шрифта. Заголовки подразделов тоже можно увеличить, но тогда они должны быть меньше заголовков разделов.',
    check(ctx) {
      const body = mainTextSize(ctx);
      if (body === undefined) return;
      let sectionSize: number | undefined;
      let reported = 0;
      for (const h of ctx.s.headings) {
        if (h.kind !== 'section' || h.inferred) continue;
        const size = paraFormat(ctx.model, para(ctx, h.paragraph)).dominantSize;
        if (size === undefined) continue;
        if (h.level === 1) {
          sectionSize = size;
          if (size > body || reported++ >= 6) continue;
          ctx.report({
            paragraph: h.paragraph,
            message: `Заголовок ${quote(label(h), 40)} набран кеглем ${fmtNum(size / 2)} пт, ${size === body ? 'как основной текст' : 'мельче основного текста'}, — заголовки разделов выделяют увеличенным размером шрифта`,
            fix: [{ kind: 'runs', paragraph: h.paragraph, set: { sz: body + 4 } }],
            fixLabel: `Кегль ${fmtNum((body + 4) / 2)} пт`,
          });
        } else if (sectionSize !== undefined && size > body && size >= sectionSize && reported++ < 6) {
          ctx.report({
            paragraph: h.paragraph,
            message: `Заголовок подраздела ${quote(label(h), 40)} набран кеглем ${fmtNum(size / 2)} пт — он должен быть меньше заголовка раздела`,
          });
        }
      }
    },
  }),

  defineRule({
    id: 'structure.heading-dot',
    category: 'structure',
    title: 'Номер раздела без точки',
    severity: 'warning',
    shortWork: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.4.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.5.4' },
    why: 'Номера разделов и подразделов пишут без точки в конце: «1 Анализ», «1.2 Методика».',
    check(ctx) {
      const wantDot = ctx.profile.headings.numberDot;
      for (const h of ctx.s.headings) {
        if (h.kind !== 'section' || !h.number || h.numberDot === wantDot) continue;
        const p = para(ctx, h.paragraph);
        let fix: FixAction[] | undefined;
        if (h.numberSource === 'text') {
          const m = /^(\s*\d{1,2}(?:\.\d{1,2})*)(\.?)/.exec(p.text);
          if (m) {
            const at = m[1]!.length;
            fix = wantDot
              ? [{ kind: 'text', paragraph: p.index, start: at, end: at, expect: '', replace: '.' }]
              : [{ kind: 'text', paragraph: p.index, start: at, end: at + 1, expect: '.', replace: '' }];
          }
        }
        const sample = wantDot ? `«${h.number}. ${h.title}»` : `«${h.number} ${h.title}»`;
        ctx.report({
          paragraph: p.index,
          message:
            (wantDot ? 'После номера раздела нужна точка: ' : 'После номера раздела точка не ставится: ') +
            sample.slice(0, 60) +
            (h.numberSource === 'auto' ? '. Номер автоматический — поправьте формат номера в настройках списка' : ''),
          fix,
          fixLabel: wantDot ? 'Добавить точку' : 'Убрать точку',
        });
      }
    },
  }),

  defineRule({
    id: 'structure.heading-numbering',
    category: 'structure',
    title: 'Последовательная нумерация разделов',
    severity: 'warning',
    shortWork: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.4.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.5.4' },
    why: 'Разделы нумеруют арабскими цифрами по порядку, подразделы — в пределах раздела: 1, 1.1, 1.2, 2…',
    check(ctx) {
      const heads = ctx.s.headings.filter((h) => h.kind === 'section' && !h.inferred && ctx.s.regions[h.paragraph] === 'main');
      let prev: number[] = [];
      let reported = 0;
      const say = (h: Heading, message: string): void => {
        if (reported++ < 6) ctx.report({ paragraph: h.paragraph, message });
      };
      for (const h of heads) {
        if (!h.number) {
          if (h.level === 1) say(h, `Раздел ${quote(h.title, 50)} без номера — разделы основной части нумеруют`);
          continue;
        }
        const parts = h.number.split('.').map(Number);
        const depth = parts.length;
        const parent = parts.slice(0, -1);
        if (depth > prev.length + 1) {
          say(h, `Заголовок ${h.number} идёт без заголовка уровнем выше`);
        } else if (!parent.every((v, i) => v === prev[i])) {
          say(h, `Подраздел ${h.number} стоит внутри раздела ${prev[0]} — номера не согласованы`);
        } else {
          const expected = depth <= prev.length ? prev[depth - 1]! + 1 : 1;
          if (parts[depth - 1] !== expected) {
            const want = [...parent, expected].join('.');
            say(h, prev.length ? `После ${prev.join('.')} идёт ${h.number} — ожидался номер ${want}` : `Нумерация разделов начинается с ${h.number}, а не с ${want}`);
          }
        }
        prev = parts;
      }
    },
  }),

  defineRule({
    id: 'structure.new-page',
    category: 'structure',
    title: 'С новой страницы',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.2.1, 6.17.3', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.2.5, 6.3.4, 6.6.5' },
    why: {
      [GOST_7_32]: 'Каждый структурный элемент, каждый раздел основной части и каждое приложение начинают с новой страницы. Надёжный способ — разрыв страницы (Ctrl+Enter) или «с новой страницы» в свойствах абзаца, а не пустые строки.',
      [GOST_2_105]: 'Содержание и каждое приложение начинают с новой страницы, разделы — рекомендуется. Надёжный способ — разрыв страницы (Ctrl+Enter) или «с новой страницы» в свойствах абзаца, а не пустые строки.',
    },
    check(ctx) {
      // Короткие работы (лабораторные, практические) целиком помещаются на нескольких страницах.
      if (SHORT_WORK_KINDS.has(ctx.s.kind)) return;
      const paras = ctx.model.paragraphs;
      for (const h of ctx.s.headings) {
        if (h.inferred) continue;
        const needs = h.kind !== 'section' || (h.level === 1 && ctx.profile.headings.newPage && ctx.s.regions[h.paragraph] === 'main');
        if (!needs) continue;
        const p = paras[h.paragraph]!;
        if (p.pageBreakBefore || p.cell) continue;
        let k = h.paragraph - 1;
        let empties = 0;
        let brokenEarlier = false;
        while (k >= 0 && paras[k]!.isEmpty) {
          if (paras[k]!.pageBreakBefore) brokenEarlier = true;
          empties++;
          k--;
        }
        if (brokenEarlier || k < 0) continue;
        ctx.report({
          paragraph: p.index,
          message:
            empties >= 2
              ? `${quote(label(h), 40)} переносится на новую страницу пустыми строками — при любой правке всё съедет`
              : `${quote(label(h), 40)} должен начинаться с новой страницы`,
          fix: [{ kind: 'para', paragraph: p.index, set: { pageBreakBefore: true } }],
          fixLabel: 'С новой страницы',
        });
      }
    },
  }),

  defineRule({
    id: 'structure.toc',
    category: 'structure',
    title: 'Содержание',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 5.4.1, 6.13', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.2.2, 6.3.8, 6.4.3' },
    why: {
      [GOST_7_32]: 'Содержание включает введение, все разделы и подразделы, заключение, список источников и приложения с номерами страниц. Надёжнее всего собрать его автоматически.',
      [GOST_2_105]: 'В содержании приводят номера и заголовки разделов (при необходимости — подразделов), приложения и библиографию с номерами страниц. Надёжнее всего собрать его автоматически.',
    },
    check(ctx) {
      const contents = ctx.s.headings.find((h) => h.structural === 'contents');
      if (!contents) return;
      const paras = ctx.model.paragraphs;
      const entries = ctx.s.tocEntries.map((i) => paras[i]!);
      const rebuild = 'Собрать содержание автоматически';
      if (!entries.length) {
        const heading = paras[contents.paragraph]!;
        ctx.report({
          paragraph: contents.paragraph,
          message: `Раздел «${titleOf(ctx.profile, 'contents')}» пустой: соберите его через «Ссылки → Оглавление»`,
          fix: heading.cell || heading.textBox ? undefined : tocFix(ctx, { after: contents.paragraph }),
          fixLabel: rebuild,
        });
        return;
      }
      const range = tocRange(ctx);
      // Пересобранное содержание — поле с гиперссылками; если заменить старое нельзя, остаётся обновление поля.
      const fix: FixAction[] | undefined = (range && tocFix(ctx, { replace: range })) ?? (ctx.s.tocAuto ? [{ kind: 'updateFields' }] : undefined);
      if (!ctx.s.tocAuto) {
        ctx.report({
          severity: 'info',
          paragraph: contents.paragraph,
          message: 'Содержание набрано вручную — номера страниц легко разойдутся с текстом. Соберите его автоматически: «Ссылки → Оглавление»',
          fix,
          fixLabel: rebuild,
        });
      }
      const clean = (s: string): string => normalizeForCompare(s.replace(/[\t .…_·]*\d{1,3}\s*$/, ''));
      const entryKeys = entries.map((p) => clean(p.text));
      const entryKeysNoNum = entryKeys.map(dropNumber);
      const keysOf = (h: Heading): string[] =>
        [normalizeForCompare(label(h)), normalizeForCompare(h.title), dropNumber(normalizeForCompare(h.title))].filter(Boolean);
      const depth = ctx.profile.headings.tocDepth;

      if (!ctx.s.tocAuto && entries.every((p) => !/\d\s*$/.test(p.text.trim()))) {
        ctx.report({
          paragraph: contents.paragraph,
          message: 'В содержании нет номеров страниц: у каждого пункта справа указывают страницу, с которой он начинается',
          fix,
          fixLabel: rebuild,
        });
      }
      let missing = 0;
      for (const h of ctx.s.headings) {
        if (h.paragraph < contents.paragraph || h.structural === 'contents' || h.inferred) continue;
        // Из приложений в содержание попадают только их заголовки, а не внутренние разделы.
        if (h.kind === 'section' && (h.level > depth || ctx.s.regions[h.paragraph] === 'appendix')) continue;
        const keys = keysOf(h);
        const found = entryKeys.some(
          (e, i) => keys.some((k) => e === k || entryKeysNoNum[i] === k || (h.kind === 'appendix' && e.startsWith(k))),
        );
        if (found || missing++ >= 8) continue;
        ctx.report({
          paragraph: h.paragraph,
          message: `Заголовка ${quote(label(h), 50)} нет в содержании${ctx.s.tocAuto ? ' — обновите оглавление' : ''}`,
          fix,
          fixLabel: fix?.some((a) => a.kind === 'toc') ? rebuild : 'Обновить оглавление при открытии',
        });
      }

      const allKeys: string[] = [];
      for (const h of ctx.s.headings) allKeys.push(...keysOf(h));
      const keySet = new Set(allKeys);
      let stale = 0;
      // Уровень пункта: по стилю «Оглавление N» или по глубине номера «1.2.3».
      const entryLevel = (p: Paragraph): number => {
        const style = ctx.model.styles.get(p.styleId)?.name ?? '';
        const byStyle = /^toc ([1-9])$/i.exec(style);
        if (byStyle) return Number(byStyle[1]);
        const num = /^\s*(\d{1,2}(?:\.\d{1,2})*)/.exec(p.text);
        return num ? num[1]!.split('.').length : 1;
      };
      entries.forEach((p, i) => {
        const e = entryKeys[i]!;
        const en = entryKeysNoNum[i]!;
        // Заголовки глубже второго уровня распознаются ненадёжно — такие пункты не сверяем.
        if (!e || keySet.has(e) || keySet.has(en) || entryLevel(p) > Math.max(2, depth)) return;
        if (allKeys.some((k) => k.length > 6 && (e.startsWith(k) || k.startsWith(en) || en.startsWith(k)))) return;
        if (stale++ >= 8) return;
        ctx.report({
          paragraph: p.index,
          message: `Пункт содержания ${quote(p.text.replace(/[\t .…_]*\d+\s*$/, ''), 50)} не совпадает ни с одним заголовком — содержание устарело`,
          fix,
          fixLabel: fix?.some((a) => a.kind === 'toc') ? rebuild : 'Обновить оглавление при открытии',
        });
      });
    },
  }),

  defineRule({
    id: 'structure.appendix',
    category: 'structure',
    title: 'Приложения',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.17', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.3' },
    why: 'Приложения обозначают буквами А, Б, В… (без Ё, З, Й, О, Ч, Ъ, Ы, Ь), дают каждому заголовок и ссылаются на все приложения в тексте.',
    check(ctx) {
      const apps = ctx.s.headings.filter((h) => h.kind === 'appendix');
      if (!apps.length) return;
      const word = titleOf(ctx.profile, 'appendix');
      const paras = ctx.model.paragraphs;
      const latin = /^[A-Z]$/.test(apps[0]!.appendixLetter ?? '');
      const alphabet = latin ? APPENDIX_LETTERS_LAT : APPENDIX_LETTERS_CYR;
      apps.forEach((h, i) => {
        const letter = h.appendixLetter;
        if (!letter) {
          ctx.report({ paragraph: h.paragraph, message: `После слова «${word}» должна стоять буква: «${word} А»` });
        } else if (!/^\d+$/.test(letter)) {
          if (!alphabet.includes(letter)) {
            ctx.report({ paragraph: h.paragraph, message: `Буква ${letter} не используется для обозначения приложений — её пропускают` });
          } else if (alphabet[i] !== letter) {
            ctx.report({ paragraph: h.paragraph, message: `Приложение ${letter} идёт ${i + 1}-м по счёту — ожидалось «${word} ${alphabet[i]}»` });
          }
        }
        const inlineTitle = paras[h.paragraph]!.text.includes('\n');
        const titles: Paragraph[] = [];
        for (let k = h.paragraph + 1; k < paras.length && ctx.s.roles[k] !== 'heading'; k++) {
          const r = ctx.s.roles[k];
          if (r === 'empty') continue;
          if (r !== 'appendix-title') break;
          if (!/^\(.*\)$/.test(cleanText(paras[k]!))) titles.push(paras[k]!);
        }
        if (!inlineTitle && !titles.length) {
          ctx.report({ paragraph: h.paragraph, message: `У приложения ${letter ?? ''} нет наименования — его пишут отдельной строкой по центру`.replace('  ', ' ') });
        }
        for (const tp of titles) {
          const fmt = paraFormat(ctx.model, tp);
          const problems: string[] = [];
          const fix: FixAction[] = [];
          if (tp.props.jc !== 'center') {
            problems.push('не по центру');
            fix.push({ kind: 'para', paragraph: tp.index, set: { jc: 'center', indFirst: 0, indLeft: 0 } });
          }
          if (fmt.boldShare < 0.6) {
            problems.push('не полужирное');
            fix.push({ kind: 'runs', paragraph: tp.index, set: { bold: true } });
          }
          if (hasTrailingPeriod(tp.text)) {
            problems.push('точка в конце');
            const f = trailingPeriodFix(tp);
            if (f) fix.push(f);
          }
          if (!startsWithUpper(tp.text)) {
            problems.push('со строчной буквы');
            const f = upperFirstFix(tp);
            if (f) fix.push(f);
          }
          if (problems.length) {
            ctx.report({ paragraph: tp.index, message: `Наименование приложения ${quote(tp.text, 40)}: ${problems.join(', ')}`, fix, fixLabel: 'Оформить наименование' });
          }
        }
      });

      const referenced = new Set<string>();
      const re = /(?<!\p{L})(?:приложени(?:е|я|ю|ем|и|ях|ям|ями)|прил\.)\s+([А-ЯA-Z](?:\s*(?:,|и|–|—|-)\s*[А-ЯA-Z])*)(?!\p{L})/gu;
      for (const p of paras) {
        const region = ctx.s.regions[p.index];
        const role = ctx.s.roles[p.index];
        if (region === 'front' || region === 'toc' || role === 'heading' || role === 'appendix-title') continue;
        for (const m of p.text.matchAll(re)) {
          const letters = m[1]!.split(/\s*(?:,|и)\s*/);
          for (const part of letters) {
            const range = /^([А-ЯA-Z])\s*[–—-]\s*([А-ЯA-Z])$/.exec(part);
            if (range) {
              const a = alphabet.indexOf(range[1]!);
              const b = alphabet.indexOf(range[2]!);
              for (let k = a; k >= 0 && k <= b; k++) referenced.add(alphabet[k]!);
            } else referenced.add(part.trim().toUpperCase());
          }
        }
      }
      for (const h of apps) {
        if (h.appendixLetter && !referenced.has(h.appendixLetter)) {
          ctx.report({ paragraph: h.paragraph, message: `На приложение ${h.appendixLetter} нет ссылки в тексте` });
        }
      }
    },
  }),

  defineRule({
    id: 'structure.appendix-status',
    category: 'structure',
    title: 'Статус приложения',
    severity: 'warning',
    gost: 'ГОСТ Р 2.105-2019, п. 6.3.4',
    why: 'Под словом «Приложение» с обозначением в скобках указывают статус: «(обязательное)», а для информационного приложения — «(рекомендуемое)» или «(справочное)».',
    check(ctx) {
      const paras = ctx.model.paragraphs;
      for (const h of ctx.s.headings) {
        if (h.kind !== 'appendix') continue;
        // Статус бывает в той же строке после Shift+Enter или в одном из следующих абзацев.
        const lines: Array<{ text: string; paragraph: number }> = paras[h.paragraph]!.text
          .split('\n')
          .slice(1)
          .map((t) => ({ text: t.trim(), paragraph: h.paragraph }));
        for (let k = h.paragraph + 1, seen = 0; k < paras.length && seen < 2; k++) {
          if (paras[k]!.isEmpty) continue;
          if (ctx.s.roles[k] === 'heading') break;
          lines.push({ text: cleanText(paras[k]!), paragraph: k });
          seen++;
        }
        const name = quote(firstLine(h.title), 30);
        const status = lines.find((l) => STATUS_RE.test(l.text));
        if (!status) {
          ctx.report({ paragraph: h.paragraph, message: `Под ${name} не указан статус: «(обязательное)», «(рекомендуемое)» или «(справочное)»` });
          continue;
        }
        const problems: string[] = [];
        if (!/^\(.*\)\.?$/.test(status.text)) problems.push('его пишут в скобках');
        if (/\p{Lu}/u.test(status.text)) problems.push('со строчной буквы');
        if (/информационное/i.test(status.text)) problems.push('для информационного приложения пишут «рекомендуемое» или «справочное»');
        if (problems.length) ctx.report({ paragraph: status.paragraph, message: `Статус приложения ${quote(status.text, 30)}: ${problems.join(', ')}` });
      }
    },
  }),

  defineRule({
    id: 'structure.heading-keep',
    category: 'structure',
    title: 'Заголовок не отрывается от текста',
    severity: 'info',
    aggregate: ['заголовок', 'заголовка', 'заголовков'],
    why: 'Заголовок не оставляют последней строкой страницы, когда текст раздела начинается на следующей. Word этого не допустит, если у заголовка включено «Не отрывать от следующего» — у стилей «Заголовок» оно включено по умолчанию.',
    check(ctx) {
      const paras = ctx.model.paragraphs;
      for (const h of ctx.s.headings) {
        const p = paras[h.paragraph]!;
        if (h.inferred || p.props.keepNext || p.pageBreakBefore || p.cell || p.textBox) continue;
        // Если следующий абзац при последней вёрстке в Word начал новую страницу — заголовок уже оторвался.
        let k = h.paragraph + 1;
        while (k < paras.length && paras[k]!.isEmpty) k++;
        const next = paras[k];
        const torn = !!next && next.renderedPageStart && !next.pageBreakBefore;
        ctx.report({
          severity: torn ? 'warning' : undefined,
          paragraph: p.index,
          message: torn
            ? `Заголовок ${quote(label(h), 40)} остался внизу страницы, а текст начинается на следующей`
            : 'Заголовок может остаться внизу страницы отдельно от текста: не включено «Не отрывать от следующего»',
          fix: [{ kind: 'para', paragraph: p.index, set: { keepNext: true } }],
          fixLabel: 'Не отрывать от следующего',
        });
      }
    },
  }),

  defineRule({
    id: 'structure.title-year',
    category: 'structure',
    title: 'Год на титульном листе',
    severity: 'info',
    why: 'На титульном листе указывают год сдачи работы. Прошлогодний год часто остаётся от старого или чужого шаблона.',
    check(ctx) {
      const { model, s } = ctx;
      const saved = Number((model.meta.modified ?? model.meta.created ?? '').slice(0, 4));
      if (!s.hasTitlePage || !saved) return;
      const years: Array<{ paragraph: number; start: number; year: number }> = [];
      for (const p of model.paragraphs.slice(0, s.frontEnd)) {
        for (const m of p.text.matchAll(/(?<!\d)(?:19|20)\d{2}(?!\d)/g)) years.push({ paragraph: p.index, start: m.index, year: Number(m[0]) });
      }
      // Год сдачи обычно в самом низу титула: «Москва 2026».
      const last = years[years.length - 1];
      if (!last || last.year >= saved) return;
      ctx.report({
        paragraph: last.paragraph,
        marks: [[last.start, last.start + 4]],
        message: `На титульном листе ${last.year} год, а файл сохранён в ${saved} году — проверьте год сдачи`,
        fix:
          years.length === 1
            ? [{ kind: 'text', paragraph: last.paragraph, start: last.start, end: last.start + 4, expect: String(last.year), replace: String(saved) }]
            : undefined,
        fixLabel: `Заменить на ${saved}`,
      });
    },
  }),

  defineRule({
    id: 'structure.list-markers',
    category: 'structure',
    title: 'Маркеры перечислений',
    severity: 'info',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.4.6', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.7.2' },
    why: {
      [GOST_7_32]: 'Перед каждым элементом перечисления ставят тире; если на элементы ссылаются — строчные буквы со скобкой а), б) или цифры 1), 2).',
      [GOST_2_105]: 'Перед каждой позицией перечисления ставят дефис; если на позиции ссылаются — строчные буквы со скобкой а), б), для детализации — цифры 1), 2).',
    },
    check(ctx) {
      const DASHES = new Set(['–', '—', '-', '−', '‒']);
      const eskd = ctx.profile.id === GOST_2_105;
      const want = eskd ? 'дефис' : 'тире';
      const dash = eskd ? '-' : '–';
      const font = ctx.profile.text.fonts[0] ?? 'Times New Roman';
      const groups = new Map<string, { first: number; count: number; label: string; fix: FixAction[] }>();
      for (const p of byRole(ctx, new Set<Role>(['list']))) {
        let marker: string | undefined;
        let key: string;
        let action: FixAction;
        if (p.list?.bullet) {
          marker = p.list.label.trim();
          key = `n${p.list.numId}:${p.list.ilvl}`;
          // Маркер меняется в определении списка — сразу у всех его пунктов.
          action = { kind: 'bullet', numId: p.list.numId, ilvl: p.list.ilvl, text: dash, font };
        } else {
          const m = /^(\s*)([•·▪●○■□◦➢✓❖])\s/.exec(p.text);
          if (!m) continue;
          marker = m[2]!;
          key = `m${marker}`;
          const at = m[1]!.length;
          action = { kind: 'text', paragraph: p.index, start: at, end: at + 1, expect: marker, replace: dash };
        }
        if (!marker || DASHES.has(marker)) continue;
        const g = groups.get(key);
        if (!g) groups.set(key, { first: p.index, count: 1, label: marker, fix: [action] });
        else {
          g.count++;
          if (action.kind === 'text') g.fix.push(action);
        }
      }
      for (const g of groups.values()) {
        ctx.report({
          paragraph: g.first,
          message: `Маркер перечисления «${g.label}» (${countOf(g.count, ['пункт', 'пункта', 'пунктов'])}) — по стандарту ставят ${want}`,
          fix: g.fix,
          fixLabel: `Заменить на ${want}`,
        });
      }
    },
  }),
];
